import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Barcode, Printer, Sparkles, Trash2, TriangleAlert } from 'lucide-react'
import { apiClient, ApiError } from '@/lib/api'
import type { Product } from '@/lib/types'
import { barcodeSvg, isValidEan13, type Symbology } from '@/lib/printing/barcode'
import { renderLabelsHtml, type ProductLabel } from '@/lib/printing/labelHtml'
import { printHtml } from '@/lib/printing/adapters'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge, Modal } from '@/components/ui/primitives'
import { t } from '@/i18n'

interface ProductBarcode {
  id: number
  code: string
  packQuantity: number
  isPrimary: boolean
}

/**
 * Gives a product a barcode and prints labels for it.
 *
 * Plenty of stock has no barcode at all — loose goods, a local supplier, anything repacked out
 * the back. Until it has one it cannot be scanned, so this is where a shop gives it one and puts
 * it on the shelf.
 */
export function LabelDialog({
  product,
  onClose,
}: {
  product: Product | null
  onClose: () => void
}) {
  const queryClient = useQueryClient()

  const [custom, setCustom] = useState('')
  const [copies, setCopies] = useState('1')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (product) {
      setCustom('')
      setCopies('1')
      setError(null)
    }
  }, [product])

  const barcodes = useQuery({
    queryKey: ['barcodes', product?.id],
    queryFn: () => apiClient.get<ProductBarcode[]>(`/api/products/${product!.id}/barcodes`),
    enabled: product !== null,
  })

  const assign = useMutation({
    mutationFn: (code: string | null) =>
      apiClient.post<ProductBarcode>(`/api/products/${product!.id}/barcodes`, {
        code,
        packQuantity: 1,
      }),
    onSuccess: async () => {
      setCustom('')
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['barcodes'] })
      await queryClient.invalidateQueries({ queryKey: ['products'] })
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not save.'),
  })

  const remove = useMutation({
    mutationFn: (id: number) => apiClient.delete(`/api/products/barcodes/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['barcodes'] })
      await queryClient.invalidateQueries({ queryKey: ['products'] })
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not remove.'),
  })

  const print = useMutation({
    mutationFn: async (code: string) => {
      // The label's price and store name come from the server, so what is printed cannot drift
      // from what the till will charge.
      const labels = await apiClient.post<ProductLabel[]>('/api/products/labels', [
        { productId: product!.id, copies: Number(copies || 1), code },
      ])
      await printHtml(renderLabelsHtml(labels))
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not print.'),
  })

  const primary = barcodes.data?.find((entry) => entry.isPrimary) ?? barcodes.data?.[0]
  const trimmed = custom.trim()

  // Twelve digits is an EAN-13 waiting for its check digit, which the server fills in — worth
  // saying so rather than letting it look like a mistake.
  const willComplete = /^\d{12}$/.test(trimmed)
  const badCheckDigit = /^\d{13}$/.test(trimmed) && !isValidEan13(trimmed)

  return (
    <Modal
      open={product !== null}
      onClose={onClose}
      wide
      title={product ? `Barcode for ${product.name}` : ''}
      footer={<Button variant="secondary" onClick={onClose}>{t('Close')}</Button>}
    >
      {/* What is already on the product */}
      {barcodes.data?.length === 0 ? (
        <p className="surface-sunken mb-4 rounded-lg px-3 py-2 text-sm text-[var(--text-secondary)]">
          {t('This product has no barcode yet, so it cannot be scanned at the till.')}
        </p>
      ) : (
        <ul className="mb-4 flex flex-col gap-2">
          {barcodes.data?.map((entry) => (
            <li
              key={entry.id}
              className="surface-raised flex items-center gap-3 rounded-lg border p-3"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="tabular truncate font-medium">{entry.code}</span>
                  {entry.isPrimary && <Badge tone="accent">{t('on the label')}</Badge>}
                  {!isValidEan13(entry.code) && <Badge tone="neutral">{t('Code 128')}</Badge>}
                </div>
                {entry.packQuantity !== 1 && (
                  <p className="text-xs text-[var(--text-secondary)]">
                    Scans as {entry.packQuantity}
                  </p>
                )}
              </div>

              <Preview code={entry.code} />

              <Button
                variant="ghost"
                size="icon"
                aria-label={`Remove ${entry.code}`}
                onClick={() => remove.mutate(entry.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {/* Adding one */}
      <section className="surface-sunken mb-4 rounded-xl p-4">
        <p className="mb-3 text-sm font-medium">{t('Add a barcode')}</p>

        <Button full onClick={() => assign.mutate(null)} disabled={assign.isPending}>
          <Sparkles className="h-4 w-4" />
          {assign.isPending ? 'Working…' : 'Generate one for me'}
        </Button>
        <p className="mt-1.5 text-xs text-[var(--text-secondary)]">
          {t('Uses the range reserved for a shop\'s own codes, so it can never clash with a barcode printed by a supplier.')}
        </p>

        <div className="my-3 flex items-center gap-3">
          <span className="h-px flex-1 bg-[var(--border-subtle)]" />
          <span className="text-xs text-[var(--text-secondary)]">{t('or type your own')}</span>
          <span className="h-px flex-1 bg-[var(--border-subtle)]" />
        </div>

        <div className="flex items-end gap-2">
          <Input
            label={t('Your code')}
            value={custom}
            onChange={(event) => setCustom(event.target.value)}
            wrapperClassName="flex-1"
            className="tabular"
            placeholder={t('Any digits or letters')}
            hint={
              willComplete
                ? 'Twelve digits — the last one will be worked out for you'
                : badCheckDigit
                  ? 'That last digit does not match; check it against the packet'
                  : undefined
            }
          />
          <Button
            variant="secondary"
            onClick={() => assign.mutate(trimmed)}
            disabled={trimmed.length === 0 || assign.isPending}
          >
            {t('Add')}
          </Button>
        </div>
      </section>

      {/* Printing */}
      {primary && (
        <section className="surface-sunken rounded-xl p-4">
          <p className="mb-3 text-sm font-medium">{t('Print labels')}</p>

          <div className="flex items-end gap-3">
            <Input
              label={t('How many')}
              type="number"
              inputMode="numeric"
              min="1"
              max="500"
              value={copies}
              onChange={(event) => setCopies(event.target.value)}
              wrapperClassName="w-32"
              className="tabular"
            />
            <Button
              onClick={() => print.mutate(primary.code)}
              disabled={print.isPending || Number(copies || 0) < 1}
            >
              <Printer className="h-4 w-4" />
              {print.isPending ? 'Sending…' : 'Print'}
            </Button>
          </div>

          <p className="mt-2 text-xs text-[var(--text-secondary)]">
            {t('One label per page at the size set in Settings, which is what a roll-fed label printer expects. Choose the label printer in the dialog that opens.')}
          </p>
        </section>
      )}

      {error && (
        <p role="alert" className="mt-4 rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
          <TriangleAlert className="mr-1 inline h-4 w-4" />
          {error}
        </p>
      )}
    </Modal>
  )
}

/** The bars as they will actually print, so a bad code is visible before a roll is wasted. */
function Preview({ code }: { code: string }) {
  const symbology: Symbology = isValidEan13(code) ? 'Ean13' : 'Code128'

  try {
    return (
      <div
        className="h-10 w-28 shrink-0"
        aria-label={`Barcode ${code}`}
        dangerouslySetInnerHTML={{ __html: barcodeSvg(code, symbology, { showText: false }) }}
      />
    )
  } catch {
    return (
      <span className="shrink-0 text-xs text-danger-500">
        <Barcode className="mr-1 inline h-4 w-4" />
        cannot print
      </span>
    )
  }
}
